import {
  ActionList,
  ActionListItem,
  Divider,
  Loader,
  SectionMessage,
  Text,
} from "@certn/cascadia-react";
import { Toggle } from "@certn/cascadia-react/components/inputs/toggle/Toggle";
import { useAPIRequest, usePeopleAPI } from "@certn/legacy-api";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { AppTitle } from "../../components/AppTitle";
import { useDocumentTitle } from "../../hooks/api/useDocumentTitle";

type NotificationOptions =
  | "completed-background-checks"
  | "expiring-soon"
  | "marketing-and-promotions";

const urlOptionToDataAttribute = {
  "completed-background-checks":
    "allow_completed_background_check_notifications",
  "expiring-soon": "allow_application_expiration_notifications",
  "marketing-and-promotions": "accept_marketing",
};

export function NotificationsPage() {
  const { t } = useTranslation();
  useDocumentTitle(`Certn ${t("Notifications")}`);
  const [updating, setUpdating] = useState<string>();

  const { data, isLoading, mutate } = usePeopleAPI(
    "/internal/people/me/preferences/notifications/"
  );
  const request = useAPIRequest();

  if (!data) {
    if (isLoading) {
      return <Loader overlay />;
    }

    return <></>;
  }

  const updateNotificationSettings = async (
    notificationType: NotificationOptions,
    enabled: boolean
  ) => {
    const path = `/internal/people/me/preferences/${notificationType}/update/`;
    const requestData = {
      method: "POST",
      body: JSON.stringify({ enabled }),
    };

    try {
      setUpdating(notificationType);
      await request(path, requestData);
      mutate({
        ...data,
        [urlOptionToDataAttribute[notificationType]]: !enabled,
      });
    } finally {
      setUpdating(undefined);
    }
  };

  return (
    <>
      <AppTitle title="Notifications" />
      <SectionMessage
        type="info"
        icon="info"
        sectionTitle={t(
          "You are unable to manage application invite and reminder emails."
        )}
      >
        <Trans>
          Application invite emails are mandatory and will always be enabled.
          The frequency of reminder emails are set by the requestor.
        </Trans>
      </SectionMessage>
      <ActionList ariaLabel={t("Notifications")}>
        <>
          <ActionListItem
            displayAccessibility
            title={t("Completed background checks")}
            trailing={
              <Toggle
                testID="completed-background-checks-notification"
                id="toggle"
                aria-label="toggle"
                isSelected={data.allow_completed_background_check_notifications}
                onChange={(value) =>
                  updateNotificationSettings(
                    "completed-background-checks",
                    value
                  )
                }
                isDisabled={updating === "completed-background-checks"}
              />
            }
          >
            <Text style="global/body-secondary" color="text-weak">
              <Trans>
                Email me when my background check has finished processing
              </Trans>
            </Text>
          </ActionListItem>
          <Divider />
          <ActionListItem
            displayAccessibility
            title={t("Expiring soon")}
            trailing={
              <Toggle
                testID="expiring-soon-notification"
                id="toggle"
                aria-label="toggle"
                isSelected={data.allow_application_expiration_notifications}
                onChange={(value) =>
                  updateNotificationSettings("expiring-soon", value)
                }
                isDisabled={updating === "expiring-soon"}
              />
            }
          >
            <Text style="global/body-secondary" color="text-weak">
              <Trans>Email me when an application will expire soon</Trans>
            </Text>
          </ActionListItem>
          <Divider />
          <ActionListItem
            displayAccessibility
            title={t("Certn updates and promotions")}
            trailing={
              <Toggle
                testID="updates-and-promotions-notification"
                id="updates-and-promotions"
                aria-label="toggle"
                isSelected={data.accept_marketing}
                onChange={(value) =>
                  updateNotificationSettings("marketing-and-promotions", value)
                }
                isDisabled={updating === "marketing-and-promotions"}
              />
            }
          >
            <Text style="global/body-secondary" color="text-weak">
              <Trans>
                Email me about news, feature updates, discounts and offers from
                Certn
              </Trans>
            </Text>
          </ActionListItem>
        </>
      </ActionList>
    </>
  );
}
